import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, startOfWeek, getISOWeek, getISOWeekYear } from 'date-fns';
import { weeksAPI } from '../services/api';
import { Todo, Week, WeekUpdate } from '../types';

export const useWeeks = (selectedDate: Date) => {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  // Get current week data
  const { data: currentWeekData, isLoading: isCurrentWeekLoading } = useQuery({
    queryKey: ['weeks', 'current'],
    queryFn: async (): Promise<Week> => {
      return await weeksAPI.getCurrent();
    },
    enabled: startOfWeek(selectedDate).getTime() === startOfWeek(new Date()).getTime(),
  });

  // Get specific week data for historical dates
  const { data: weekData, isLoading: isWeekLoading } = useQuery({
    queryKey: ['weeks', format(startOfWeek(selectedDate), 'yyyy-MM-dd')],
    queryFn: async (): Promise<Week | null> => {
      try {
        const weekStart = startOfWeek(selectedDate);
        // Get all weeks and find the matching one
        const weeks = await weeksAPI.getAll(undefined, 50, 0);
        
        const weekMatch = weeks.find((w: Week) => 
          w.year === getISOWeekYear(weekStart) && 
          w.week_number === getWeekNumber(weekStart)
        );

        return weekMatch || null;
      } catch (err) {
        console.error('Error fetching week data:', err);
        return null;
      }
    },
    enabled: startOfWeek(selectedDate).getTime() !== startOfWeek(new Date()).getTime(),
  });

  // Update week mutation
  const updateWeekMutation = useMutation({
    mutationFn: async ({ weekId, data }: { weekId: string; data: WeekUpdate }) => {
      return await weeksAPI.update(weekId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weeks'] });
    },
    onError: (err: unknown) => {
      const errorMessage = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to update week';
      setError(errorMessage);
    },
  });

  // Update week function
  const updateWeek = async (weekId: string, data: WeekUpdate) => {
    return updateWeekMutation.mutateAsync({ weekId, data });
  };

  // Helper function to get ISO week number
  const getWeekNumber = (date: Date): number => {
    return getISOWeek(date);
  };

  // Determine which data to use based on selected date
  const isCurrentWeek = startOfWeek(selectedDate).getTime() === startOfWeek(new Date()).getTime();
  const currentData = isCurrentWeek ? currentWeekData : weekData;
  const isLoading = isCurrentWeek ? isCurrentWeekLoading : isWeekLoading;

  // For now, return empty completed tasks as the API structure doesn't include them
  // This will need to be implemented properly when the weeks API supports task completion tracking
  return {
    currentWeek: currentData || null,
    completedTasks: [] as Todo[],
    updateWeek,
    isLoading,
    error,
    isUpdating: updateWeekMutation.isPending,
  };
};